<template>
    <div class="container">
        <section id="service-intro" class="section service-intro">
            <h1 class="title">No ofrecemos servicios, resolvemos lo que nadie más se atreve a intentar.</h1>
            <p class="service-intro-text">
                Trabajamos en tres áreas que se complementan: fabricamos tus piezas
                con impresión 3D, automatizamos los procesos que hoy te consumen
                tiempo y construimos el software que sostiene tu operación.
            </p>
            <div class="service-intro-actions">
                <WhatsAppButton
                    variant="inline"
                    label="Cuéntanos por WhatsApp"
                    message="Hola, quiero información sobre sus servicios."
                />
                <router-link to="/catalog" class="service-intro-link">Mira lo que hemos construido →</router-link>
            </div>

            <img src="../assets/SelkService.svg" alt="Las tres áreas de servicio de Selk" />
        </section>

        <section
            v-for="(area, index) in areas"
            :id="area.id"
            :key="area.id"
            class="section area-block"
            :class="{ 'area-block--reverse': index % 2 === 1 }"
        >
            <div class="area-media">
                <picture>
                    <img :src="area.image" :alt="area.imageAlt" loading="lazy" decoding="async" />
                </picture>
            </div>

            <div class="area-content">
                <span class="area-tag">{{ area.tag }}</span>
                <h2 class="area-title">{{ area.title }}</h2>
                <p class="area-text">{{ area.text }}</p>

                <ul class="area-points">
                    <li v-for="point in area.points" :key="point">{{ point }}</li>
                </ul>

                <router-link :to="area.to" class="button">{{ area.cta }}</router-link>
            </div>
        </section>
    </div>
</template>

<script>
import WhatsAppButton from './WhatsAppButton.vue';

export default {
    name: 'ServicePage',
    components: { WhatsAppButton },
    data() {
        return {
            // Tres áreas clave (plan 22_08_2026, R1). Los `id` son las anclas a
            // las que apunta la navegación de la cabecera.
            areas: [
                {
                    id: 'area-impresion3d',
                    tag: 'Área 1',
                    title: 'Impresión 3D',
                    text: 'Diseñamos y fabricamos la pieza que necesitas: del modelado al objeto terminado, ya sea un prototipo funcional o una producción bajo demanda.',
                    points: [
                        'Modelado de piezas mecánicas para ingeniería',
                        'Prototipado funcional de productos',
                        'Fabricación e impresión 3D bajo demanda',
                        'Ajustes de material, tamaño y acabado',
                    ],
                    cta: 'Cotiza tu impresión 3D',
                    to: '/print3d',
                    image: require('../assets/workingPeople.svg'),
                    imageAlt: 'Diseño y fabricación de piezas mediante impresión 3D',
                },
                {
                    id: 'area-automatizacion',
                    tag: 'Área 2',
                    title: 'Automatización de procesos',
                    text: 'Convertimos las tareas manuales que te consumen el día en flujos que corren solos, con la información de tu negocio ordenada y a la mano.',
                    points: [
                        'Automatización y seguimiento de procesos',
                        'Sistemas de gestión de información',
                        'Integración con las herramientas que ya usas',
                        'Diagnóstico y optimización de la producción',
                    ],
                    cta: 'Hablemos de tu proceso',
                    to: '/contact',
                    image: require('../assets/SelkMake.svg'),
                    imageAlt: 'Automatización y optimización de procesos empresariales',
                },
                {
                    id: 'area-aplicativos',
                    tag: 'Área 3',
                    title: 'Aplicativos web',
                    text: 'Construimos el software a la medida de tu operación y también la presencia web con la que te encuentran tus clientes: rápida, clara y pensada para convertir.',
                    points: [
                        'Plataformas a medida para clientes y equipo',
                        'Webs corporativas y landing pages',
                        'E-commerce y portafolios digitales',
                        'Sitios autoadministrables',
                    ],
                    cta: 'Cuéntanos tu proyecto',
                    to: '/contact',
                    image: require('../assets/webPageService.svg'),
                    imageAlt: 'Desarrollo de aplicativos y sitios web a medida',
                },
            ],
        };
    },
}

</script>

<style scoped>
/* ---------- Intro ---------- */
.service-intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.service-intro h1 {
    margin: var(--space-md) 0 var(--space-sm) 0;
}

.service-intro-text {
    font-family: var(--font-body);
    font-size: 20px;
    line-height: 1.5;
    color: var(--color-text);
    max-width: 48rem;
    margin: 0 0 var(--space-md) 0;
}

.service-intro-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    margin-bottom: var(--space-md);
}

.service-intro-link {
    font-family: var(--font-body);
    font-size: 18px;
    color: var(--color-action);
    text-decoration: none;
    border-bottom: 2px solid transparent;
    transition: color 0.2s ease, border-color 0.2s ease;
}

.service-intro-link:hover {
    color: var(--color-brand);
    border-bottom-color: var(--color-brand);
}

.service-intro img {
    width: 100%;
    max-width: 720px;
    height: auto;
}

/* ---------- Bloques de área ---------- */
.area-block {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--space-xl);
    /* El header es fijo: evita que el ancla quede oculta bajo la cabecera. */
    scroll-margin-top: calc(var(--header-offset) + var(--space-sm));
}

.area-block--reverse {
    flex-direction: row-reverse;
}

.area-media {
    flex: 1 1 45%;
    min-width: 0;
}

/* Marco de proporción fija: cambiar el activo por la fotografía profesional
   no exige tocar estilos. */
.area-media picture {
    display: block;
    aspect-ratio: 4 / 3;
    background-color: var(--color-surface);
    border-radius: 16px;
    overflow: hidden;
}

.area-media img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: var(--space-sm);
    box-sizing: border-box;
}

.area-content {
    flex: 1 1 55%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.area-tag {
    font-family: var(--font-body);
    font-size: 15px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-brand);
    margin-bottom: var(--space-xs);
}

.area-title {
    font-family: var(--font-subtitle);
    font-weight: 700;
    font-size: 40px;
    line-height: 1.1;
    color: var(--color-title);
    margin: 0 0 var(--space-sm) 0;
}

.area-text {
    font-family: var(--font-body);
    font-size: 19px;
    line-height: 1.5;
    color: var(--color-text);
    margin: 0 0 var(--space-md) 0;
}

.area-points {
    list-style: none;
    margin: 0 0 var(--space-md) 0;
    padding: 0;
}

.area-points li {
    font-family: var(--font-body);
    font-size: 18px;
    line-height: 1.4;
    color: var(--color-text);
    padding-left: 1.6rem;
    position: relative;
    margin-bottom: var(--space-xs);
}

.area-points li::before {
    content: '✔';
    position: absolute;
    left: 0;
    top: 0.15em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: var(--color-accent);
    color: var(--color-white);
    font-size: 10px;
}

.area-content .button {
    margin: 0;
}

@media (max-width: 1024px) {
    .area-block,
    .area-block--reverse {
        flex-direction: column;
        gap: var(--space-md);
    }
    .area-media,
    .area-content {
        flex: 1 1 auto;
        width: 100%;
    }
}

@media (max-width: 800px) {
    .area-title {
        font-size: 1.75rem;
        text-align: center;
        margin-bottom: var(--space-md);
    }
    .area-content {
        align-items: center;
        text-align: center;
    }
    .area-tag {
        align-self: center;
    }
    .service-intro img {
        content: url(../assets/SelkService_Movile.svg);
    }
}
</style>
